import React, { useContext, useEffect, useMemo, useState } from 'react';
import MediaQuery from 'react-responsive';
import styled from 'styled-components';
import AddDesignerButton from '@99designs/apps/spa/pages/create/components/AddDesignerButton';
import { DesignerCard_UserFragment } from '@99designs/apps/spa/pages/create/components/DesignerCard.generated';
import { trackBriefModalViewedEvent } from '@99designs/apps/spa/pages/create/events';
import { Card } from '@99designs/brief';
import { BriefContext } from '@99designs/brief';
import { DesignCarousel, DesignerMeta } from '@99designs/designers';
// eslint-disable-next-line no-restricted-imports
import { breakpoints } from '@99designs/emu/settings';
import { Desktop } from '@99designs/frontend-components/components/Desktop';
import { Mobile } from '@99designs/frontend-components/components/Mobile';
import { DesignerSource } from '@99designs/graph-utils/types';
import DesignerCardContext from './DesignerCardContext';
import FileOverlay from './FileOverlay';

interface DesignerDetailsProps {
    hasStatsSection: boolean;
}

const DesignerDetails = styled.div`
    display: flex;
    flex-grow: 1;
    min-height: ${(props: DesignerDetailsProps) => (props.hasStatsSection ? '205px' : '155px')};
`;

export interface Props {
    user: DesignerCard_UserFragment;
    isSelected: boolean;
    onMount: () => void;
    designerSource: DesignerSource;
    position: number;
    spokenLanguage?: string;
}

export default function DesignerCard(props: Props) {
    const { user, isSelected, onMount, position, designerSource } = props;

    useEffect(() => {
        onMount();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [isOverlayActive, updateIsOverlayActive] = useState(false);
    const [showFullBiography, updateShowFullBiography] = useState(false);
    const [activeDesign, updateActiveDesign] = useState(0);
    const { brief, totalAllowableDesigners, schema } = useContext(BriefContext);
    const { stats = [], selectDesignerLabels } = useContext(DesignerCardContext);
    const cardVariations = ['designer-card'];

    const displayDesignerProfileOverlay = () => {
        trackBriefModalViewedEvent({
            id: brief.id,
            modal: 'DesignerProfile',
            schema_id: schema,
        });
        updateIsOverlayActive(true);
    };

    return (
        <Card
            variations={isSelected ? [...cardVariations, 'selected'] : cardVariations}
            data-testid="designer-card"
        >
            <Card.Media>
                <MediaQuery minWidth={breakpoints.normal[0]}>
                    {(matches) => (
                        <DesignCarousel
                            displayName={user.displayName}
                            designs={user.profile.designs}
                            isClickable={matches}
                            showPaginators={false}
                            onClick={displayDesignerProfileOverlay}
                            onChange={(designIndex) => {
                                updateActiveDesign(designIndex);
                            }}
                            selectedIndex={activeDesign}
                        />
                    )}
                </MediaQuery>
                {isOverlayActive && (
                    <FileOverlay
                        designer={user}
                        selectedDesignIndex={activeDesign}
                        onClose={() => updateIsOverlayActive(false)}
                        onDesignChange={(designIndex) => {
                            updateActiveDesign(designIndex);
                        }}
                    />
                )}
            </Card.Media>
            <DesignerDetails hasStatsSection={stats.length > 0}>
                <Card.Block>
                    <Desktop>
                        <DesignerMeta
                            designer={user}
                            onClickDescription={displayDesignerProfileOverlay}
                            stats={stats}
                            spokenLanguage={props.spokenLanguage}
                        />
                    </Desktop>
                    <Mobile>
                        <DesignerMeta
                            designer={user}
                            onClickDescription={() => {
                                updateShowFullBiography(!showFullBiography);
                            }}
                            showFullText={showFullBiography}
                            stats={stats}
                            spokenLanguage={props.spokenLanguage}
                        />
                    </Mobile>
                </Card.Block>
            </DesignerDetails>
            <Card.Block>
                <AddDesignerButton
                    designer={user}
                    position={position}
                    designerSource={designerSource}
                    selectDesignerLabels={selectDesignerLabels}
                    brief={brief}
                    totalAllowableDesigners={totalAllowableDesigners}
                />
            </Card.Block>
        </Card>
    );
}
